<template>
  <Loader v-if="isLoading"></Loader>
  <div class="s-movie-views__stip-container">
    <div class="container">
      <div class="s-movie-views__top-area">
        <div class="s-movie-views__title">
          {{ dictionary.views }}
        </div>

        <div class="s-movie-views__el">
          <ScreeningsFilters
            v-if="desktop"
            v-model="selectedScreeningsFilters"
            :room-types-options="screeningsFiltersOptions.roomTypes"
            :screen-types-options="screeningsFiltersOptions.screenTypes"
            @filter-changed="onChangeFilter" />
        </div>

        <div class="s-movie-views__el-mob-btn">
          <button
            type="button"
            class="s-movie-views__filter-btn"
            @click="() => (showMobileFiltersModal = true)"></button>
        </div>
      </div>

      <div class="s-movie-views__container">
        <div class="s-movie-views__left-col">
          <div class="form-chunk-cinema-list">
            <div
              v-for="item in cinemas"
              :key="item.value"
              class="form-chunk-cinema">
              <input
                :id="'cinema' + item.value"
                class="form-check-input"
                type="radio"
                name="types"
                :value="item.value"
                :checked="item.value === selectedCinema"
                :disabled="!item.isOnline"
                @change="onChangeCinema($event.target.value)" />
              <label class="form-check-label" :for="'cinema' + item.value">
                <span>
                  {{ item.display }}
                </span>
                <div class="address">
                  <em v-html="getAddressPlace(item.address)"></em>
                  <em v-html="getAddressArea(item.address)"></em>
                </div>
              </label>
            </div>
          </div>
        </div>
        <div class="s-movie-views__right-col">
          <CalendarSlider
            v-model="selectedFilters.date"
            :local="getLocale()"
            :breakpoints="calenderBreakPoints"
            :date-formate-options="{
              weekday: 'short',
              month: 'short',
              day: 'numeric',
            }"
            :allowed-dates="allowedDates"
            @choose-date="chooseDate" />

          <Screenings
            :screenings="filteredScreens"
            :selected-screen="selectedFilters.screen"
            show-pre-booking-preview>
            <template #screen-features="{ screen }">
              <ScreenTypes
                :sphera="false"
                :dolby="screen.isDolby"
                :three-dimensions="screen.is3D"
                :imax="screen.isImax"
                :imax3d="screen.isImax3D"
                container-class="e-rooms--no-background" />
            </template>
            <template #room-type="{ screen }">
              <RoomTypeNames
                :screen="screen"
                :room-filters="filters.roomTypes" />
            </template>
            <template #action="{ currentScreen }">
              <button
                type="button"
                class="b-calender__screening-cta"
                :disabled="currentScreen.soldoutStatus"
                @click="chooseScreen(currentScreen)">
                <span>{{ dictionary.ticketsCta }}</span>
              </button>
            </template>
          </Screenings>
        </div>
      </div>
    </div>
  </div>
  <ModalOverlay v-model="showMobileFiltersModal">
    <template #header>
      <i class="icon-filters"></i>
      <div class="b-pop-up__header__title">{{ dictionary.filters }}</div>
    </template>
    <template #body>
      <div class="b-pop-up__content">
        <ScreeningsFilters
          v-model="selectedScreeningsFilters"
          :room-types-options="screeningsFiltersOptions.roomTypes"
          :screen-types-options="screeningsFiltersOptions.screenTypes"
          @filter-changed="onChangeFilter" />
        <div class="b-pop-up__btn-area">
          <button
            type="submit"
            class="b-pop-up__btn-mobile"
            @click="closeFilterModal">
            {{ dictionary.result }}
          </button>
          <button
            type="reset"
            class="b-pop-up__btn-reset"
            @click="onClickClearFilters">
            {{ dictionary.clearFilter }}
          </button>
        </div>
      </div>
    </template>
  </ModalOverlay>
</template>

<script setup>
import { computed, getCurrentInstance, nextTick, onBeforeMount, ref } from 'vue'
import ScreeningsFilters from '../components/ScreeningsFilters.vue'
import useViewport from '../../features/viewport'
import { getData, getLocale } from '../../lib/lib'
import Loader from '../components/Shared/Loader.vue'
import CalendarSlider from '../components/CalendarSlider.vue'
import Screenings from '../components/Screenings.vue'
import ModalOverlay from '../components/ModalOverlay.vue'
import bookingRedirect from '../../lib/bookingRedirect'
import ScreenTypes from '../components/Shared/ScreenTypes.vue'
import RoomTypeNames from '../components/RoomTypeNames.vue'
import { filterAllowedDates, filterScreens } from '../../lib/filtering'
import postRequestHandler from '../../lib/postRequestHandler'
import { sortBy, without } from 'underscore'
import constants from './../../lib/constants'
const { desktop } = useViewport()
const { redirectIntoBooking } = bookingRedirect()
const { filters, screens, records } = getData('movieDetailsData')

const { dictionary, configuration } =
  getCurrentInstance().appContext.config.globalProperties

const movieDetails = records[0]

const filterLastClicked = ref(null)
const filteredScreens = ref([])
const isLoading = ref(false)
const preSelectedCinema = ref(configuration.cinemaId)

const calenderBreakPoints = {
  175: {
    slidesPerView: 1.2,
    spaceBetween: 8,
  },
  320: {
    slidesPerView: 2.25,
    spaceBetween: 8,
  },
  576: {
    slidesPerView: 3.5,
    spaceBetween: 8,
  },
  768: {
    slidesPerView: 6,
    spaceBetween: 8,
  },
  992: {
    slidesPerView: 6,
    spaceBetween: 8,
  },
  1020: {
    slidesPerView: 6,
    spaceBetween: 8,
  },
  1267: {
    slidesPerView: 6,
    spaceBetween: 8,
  },
  1279: {
    slidesPerView: 6,
    spaceBetween: 8,
  },
}

const selectedCinema = ref('')

const screeningsFiltersOptions = ref({
  roomTypes: [],
  screenTypes: [],
})

const selectedScreeningsFilters = ref({
  roomTypes: 'All',
  screenTypes: 'All',
})

const screeningsFiltersNames = [
  constants.filters.roomTypes,
  constants.filters.screenTypes,
]

const selectedFilters = ref({
  cinema: '',
  date: '',
  screen: {},
  movieId: movieDetails?.movieId,
})

const cinemas = ref([])
const cinemasOnline = ref([])

const showMobileFiltersModal = ref(false)

const allowedDates = computed(() => {
  return filterAllowedDates(
    selectedScreeningsFilters.value.roomTypes,
    selectedScreeningsFilters.value.screenTypes,
    selectedFilters.value.cinema,
    movieDetails,
    screens,
    configuration.movieTimeOffset
  )
})

onBeforeMount(async () => {
  getMovieCinemas()

  await setPreselectedOptions()

  await nextTick()
  sessionStorage.getItem('selectedDate')
    ? await chooseDate(sessionStorage.getItem('selectedDate'))
    : await chooseDate(allowedDates.value[0])
})

// methods

const setPreselectedOptions = async () => {
  if (cinemasOnline.value.length && !sessionStorage.getItem('selectedCinema')) {
    if (preSelectedCinema.value) {
      onChangeCinema(preSelectedCinema.value)
      return
    }

    const firstCinema = cinemasOnline.value[0]
    selectedCinema.value = firstCinema.value
    await chooseCinema(firstCinema)
  }

  if (sessionStorage.getItem('selectedCinema')) {
    selectedCinema.value = sessionStorage.getItem('selectedCinema')
    onChangeCinema(selectedCinema.value)
  }

  if (sessionStorage.getItem('selectedDate')) {
    selectedFilters.value.date = sessionStorage.getItem('selectedDate')
    await chooseDate(selectedFilters.value.date)
  }
}

const getMovieCinemas = () => {
  cinemas.value = filters.cinemas.filter((cinema) => {
    return movieDetails.cinemas.includes(cinema.value)
  })

  cinemasOnline.value = cinemas.value.filter((i) => i.isOnline)
}

const getAddressPlace = (address) => {
  return address.length && address.split(',')[0]
}

const getAddressArea = (address) => {
  return address.length && address.split(',')[1]
}

const onChangeCinema = async (value) => {
  selectedCinema.value = value

  const cinema = cinemas.value.find((i) => i.value === value)

  if (cinema) {
    await chooseCinema(cinema)
  }
}

const chooseCinema = async (cinema) => {
  selectedFilters.value.cinema = cinema.value
  await nextTick()
  setScreeningsFilters()
  resetSelectedScreeningsFilters()
  await chooseDate(allowedDates.value[0])
}

const chooseDate = async (date) => {
  if (date) {
    if (typeof date === 'string') {
      selectedFilters.value.date = date
    } else {
      selectedFilters.value.date = date.dateValue
    }
  } else {
    selectedFilters.value.date = null
  }
  await setFilteredCinemaScreens()
}

const onChangeFilter = async (value, name) => {
  filterLastClicked.value = name

  selectedScreeningsFilters.value[name] = value

  chooseDate(allowedDates.value[0])
  setScreeningsFilters()
  await setFilteredCinemaScreens()
}

const setScreeningsFilters = () => {
  screeningsFiltersNames.forEach((element) => {
    if (element === filterLastClicked.value) {
      return
    }

    // filter by cinema
    let entries = filters[element].filter((i) =>
      i.filters.cinemas.includes(selectedFilters.value.cinema)
    )

    const others = without(screeningsFiltersNames, element)

    others.forEach((x) => {
      const value = selectedScreeningsFilters.value[x]

      if (value === 'All') {
        return
      }
      entries = entries.filter((i) => i.filters[x].includes(value))
    })

    screeningsFiltersOptions.value[element] = entries
  })
}

const resetSelectedScreeningsFilters = () => {
  selectedScreeningsFilters.value.roomTypes = 'All'
  selectedScreeningsFilters.value.screenTypes = 'All'
}

const getSelectedDateValue = () => {
  const result = filters.dates.find((dateFilter) => {
    const { date } = selectedFilters.value
    return dateFilter.value === date
  })?.value
  return result
}

const chooseScreen = (screen) => {
  sessionStorage.setItem('selectedCinema', selectedFilters.value.cinema)
  sessionStorage.setItem('selectedDate', selectedFilters.value.date)

  redirectIntoBooking(
    configuration.baseUrl,
    selectedFilters.value.movieId,
    selectedFilters.value.cinema,
    getSelectedDateValue(),
    screen?.id
  )
}

const getAllCinemaScreens = computed(() => {
  const { cinema, movieId, date } = selectedFilters.value

  return screens.filter((x) => {
    const sessionBusinessDatePart = x.sessionBusinessDate.split('T')[0]
    return (
      x.cinemaId === cinema &&
      x.scheduledFilmId === movieId &&
      sessionBusinessDatePart === date
    )
  })
})

const setFilteredCinemaScreens = async () => {
  const { roomTypes, screenTypes } = selectedScreeningsFilters.value
  const screens = sortBy(getAllCinemaScreens.value, 'showtime')
  const entries = filterScreens(
    roomTypes,
    screenTypes,
    screens,
    configuration.movieTimeOffset
  )

  const { cinema, date, movieId } = selectedFilters.value

  const postData = {
    filmId: movieId,
    cinemaId: cinema,
    date: date,
  }

  isLoading.value = true
  await postRequestHandler({
    postData,
    endpoint: configuration.endpointScreenAvailability,
    successCallback: (response) => {
      if (response.hasError) {
        entries.forEach((entry) => {
          entry.soldoutStatus = 1
          entry.isLimited = true
        })
      } else {
        entries.forEach((entry) => {
          const screen = response.data.availability.find(
            (x) => x.screenId === entry.id
          )
          entry.soldoutStatus = screen?.soldoutStatus
          entry.isLimited = screen?.isLimited
        })
      }
      filteredScreens.value = entries
      isLoading.value = false
    },
    errorCallback: () => {
      entries.forEach((entry) => {
        entry.soldoutStatus = 1
        entry.isLimited = true
      })
      filteredScreens.value = entries
      isLoading.value = false
    },
    refreshTokenHandler: configuration.refresh,
  })
}

const closeFilterModal = () => {
  showMobileFiltersModal.value = false
}

const onClickClearFilters = async () => {
  resetSelectedScreeningsFilters()
  closeFilterModal()
  await setFilteredCinemaScreens()
}
</script>
